import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Icon } from "../../graphic/Icon";
import style from "./style.scss";

const msgKey = "pricePlan";

export const PricePlan = ({
    title,
    tagline,
    description,
    price,

    hidePrice,
    currency,
    addTax,
    discount,
    savePercent,

    features,
    quotas,
    qualifiers,

    status,
    variant,
    children
}) => {
    return (
        <div
            className={classNames(style.base, {
                [style.variantCurrent]: variant === "current",
                [style.variantDisabled]: variant === "disabled",
                [style.variantHighlighted]: variant === "highlighted"
            })}
        >
            {/* HEADER */}
            <Header
                status={status}
                title={title}
                tagline={tagline}
                description={description}
                price={price}
                savePercent={savePercent}
                discount={discount}
                hidePrice={hidePrice}
                currency={currency}
                addTax={addTax}
                variant={variant}
            />

            {/* ACTION */}
            {children ? <div className={style.action}>{children}</div> : null}

            {/* Features */}
            <FeatureList features={features} />

            {/* Quotas */}
            <QuotaList quotas={quotas} />

            {/* Qualifiers */}
            <Qualifiers qualifiers={qualifiers} />
        </div>
    );
};

const Header = ({
    status,
    title,
    tagline,
    description,
    price,
    savePercent,
    discount,
    hidePrice,
    currency,
    addTax,
    variant
}) => {
    let priceTens, priceDecimals;
    if (price) {
        priceTens = Math.floor(price);
        priceDecimals = Number.isInteger(price) ? null : String((price % 1).toFixed(2)).slice(2);
    }

    return (
        <div className={style.header}>
            {/* Status */}
            {status === "canceled" ? (
                <div className={style.status}>
                    <span className={classNames(style.statusBadge, style[status])}>
                        <FormattedMessage id={`${msgKey}.canceled`} />
                    </span>
                </div>
            ) : null}

            {/* Title and badge */}
            <div className={style.titleRow}>
                <div className={style.title}>{title}</div>
                {variant === "current" ? (
                    <span className={classNames(style.badge, style.badgeSuccess)}>
                        <FormattedMessage id={`${msgKey}.current`} />
                    </span>
                ) : variant === "highlighted" ? (
                    <span className={style.badge}>
                        <FormattedMessage id={`${msgKey}.mostPopular`} />
                    </span>
                ) : savePercent ? (
                    <span className={classNames(style.badge, style.badgeSoft)}>
                        <FormattedMessage id={`${msgKey}.savePercent`} values={{ percent: savePercent }} />
                    </span>
                ) : discount ? (
                    <span className={classNames(style.badge, style.badgeSoft)}>
                        <FormattedMessage id={`${msgKey}.discount`} values={{ percent: discount }} />
                    </span>
                ) : null}
            </div>
            {tagline ? <div className={style.tagline}>{tagline}</div> : null}

            {/* Pricing */}
            {hidePrice ? (
                <div className={style.pricing}>
                    <div className={style.amount}>
                        <FormattedMessage id={`${msgKey}.contactUs`} />
                    </div>
                    <div className={style.description}>
                        <FormattedMessage id={`${msgKey}.contactUsDescription`} />
                    </div>
                </div>
            ) : (
                <div className={style.pricing}>
                    <div className={style.amount}>
                        <CurrencyPrefix currency={currency} />
                        {priceTens}
                        {priceDecimals && priceDecimals !== "00" ? (
                            <span className={style.decimals}>.{priceDecimals}</span>
                        ) : null}
                        <CurrencySuffix currency={currency} />
                        {addTax ? (
                            <span className={style.vat}>
                                <FormattedMessage id={`${msgKey}.exVat`} />
                            </span>
                        ) : null}
                    </div>
                    <div className={style.description}>{description}</div>
                </div>
            )}
        </div>
    );
};

const FeatureList = ({ features }) => {
    const { userCountTo, vesselCountTo, orgModule, supportLevel, paymentMethods } = features || {};
    return (
        <ul className={style.features}>
            {/* Users */}
            {userCountTo ? (
                <FeatureEntry>
                    {userCountTo >= 100 ? (
                        <FormattedMessage id={`${msgKey}.userCountUnlimited`} />
                    ) : (
                        <FormattedMessage
                            id={`${msgKey}.userCountTo`}
                            values={{ value: userCountTo, strong: (msg) => <strong>{msg}</strong> }}
                        />
                    )}
                </FeatureEntry>
            ) : null}

            {/* Vessels */}
            {vesselCountTo ? (
                <FeatureEntry>
                    {vesselCountTo >= 100 ? (
                        <FormattedMessage id={`${msgKey}.vesselCountUnlimited`} />
                    ) : (
                        <FormattedMessage
                            id={`${msgKey}.vesselCountTo`}
                            values={{ value: vesselCountTo, strong: (msg) => <strong>{msg}</strong> }}
                        />
                    )}
                </FeatureEntry>
            ) : null}

            {/* Org module */}
            {orgModule ? (
                <FeatureEntry>
                    <FormattedMessage id={`${msgKey}.orgModule`} />
                </FeatureEntry>
            ) : null}

            {/* Support */}
            {supportLevel ? (
                <FeatureEntry>
                    {supportLevel === 1 ? (
                        <FormattedMessage id={`${msgKey}.limitedSupport`} />
                    ) : supportLevel === 2 ? (
                        <FormattedMessage id={`${msgKey}.standardSupport`} />
                    ) : (
                        <FormattedMessage id={`${msgKey}.prioritySupport`} />
                    )}
                </FeatureEntry>
            ) : null}

            {/* Payment methods */}
            {paymentMethods?.includes("card") ? (
                <FeatureEntry>
                    <FormattedMessage id={`${msgKey}.paymentWithCard`} />
                </FeatureEntry>
            ) : paymentMethods?.includes("all") ? (
                <FeatureEntry>
                    <FormattedMessage id={`${msgKey}.paymentWithCardOrInvoice`} />
                </FeatureEntry>
            ) : null}
        </ul>
    );
};

const QuotaList = ({ quotas }) => {
    const { maintenanceItemsQuota, checklistsQuota, coursesQuota, deviationsQuota, storageMbQuota, documentsQuota } =
        quotas || {};
    return Object.values(quotas || {}).some((value) => value !== undefined) ? (
        <div className={style.included}>
            <div className={style.includedTitle}>
                <FormattedMessage id={`${msgKey}.includes`} />
            </div>
            <ul>
                {/* Storage */}
                {storageMbQuota ? (
                    <li>
                        <FormattedMessage
                            id={
                                storageMbQuota >= 10000
                                    ? `${msgKey}.storageMbQuotaUnlimited`
                                    : `${msgKey}.storageMbQuota`
                            }
                            values={{
                                value: storageMbQuota >= 1000 ? Math.round(storageMbQuota / 1000) : storageMbQuota,
                                unit: storageMbQuota >= 1000 ? "GB" : "MB",
                                strong: (msg) => <strong>{msg}</strong>
                            }}
                        />
                    </li>
                ) : null}

                {/* Documents */}
                {documentsQuota ? (
                    <li>
                        <FormattedMessage
                            id={
                                documentsQuota >= 5000
                                    ? `${msgKey}.documentsQuotaUnlimited`
                                    : `${msgKey}.documentsQuota`
                            }
                            values={{
                                value: documentsQuota,
                                strong: (msg) => <strong>{msg}</strong>
                            }}
                        />
                    </li>
                ) : null}

                {/* Maintenance items */}
                {maintenanceItemsQuota ? (
                    <li>
                        <FormattedMessage
                            id={
                                maintenanceItemsQuota >= 1000
                                    ? `${msgKey}.maintenanceItemsQuotaUnlimited`
                                    : `${msgKey}.maintenanceItemsQuota`
                            }
                            values={{ value: maintenanceItemsQuota, strong: (msg) => <strong>{msg}</strong> }}
                        />
                    </li>
                ) : null}

                {/* Checklists */}
                {checklistsQuota ? (
                    <li>
                        <FormattedMessage
                            id={
                                checklistsQuota >= 100
                                    ? `${msgKey}.checklistsQuotaUnlimited`
                                    : `${msgKey}.checklistsQuota`
                            }
                            values={{ value: checklistsQuota, strong: (msg) => <strong>{msg}</strong> }}
                        />
                    </li>
                ) : null}

                {/* Courses */}
                {coursesQuota ? (
                    <li>
                        <FormattedMessage
                            id={coursesQuota >= 100 ? `${msgKey}.coursesQuotaUnlimited` : `${msgKey}.coursesQuota`}
                            values={{ value: coursesQuota, strong: (msg) => <strong>{msg}</strong> }}
                        />
                    </li>
                ) : null}

                {/* Deviations */}
                {deviationsQuota ? (
                    <li>
                        <FormattedMessage
                            id={
                                deviationsQuota >= 1000
                                    ? `${msgKey}.deviationsQuotaUnlimited`
                                    : `${msgKey}.deviationsQuota`
                            }
                            values={{ value: deviationsQuota, strong: (msg) => <strong>{msg}</strong> }}
                        />
                    </li>
                ) : null}
            </ul>
        </div>
    ) : null;
};

const Qualifiers = ({ qualifiers }) => {
    const { vesselLengthBelow, vesselGrossTonnageBelow } = qualifiers || {};
    return qualifiers ? (
        <div className={style.limitations}>
            {vesselLengthBelow ? (
                <FormattedMessage
                    id={`${msgKey}.vesselLimitBelowXMeters`}
                    values={{
                        value: vesselLengthBelow,
                        strong: (msg) => <strong>{msg}</strong>
                    }}
                />
            ) : vesselGrossTonnageBelow ? (
                <FormattedMessage
                    id={`${msgKey}.vesselLimitBelowXTonnage`}
                    values={{
                        value: vesselGrossTonnageBelow,
                        strong: (msg) => <strong>{msg}</strong>
                    }}
                />
            ) : (
                <FormattedMessage id={`${msgKey}.noVesselLimitations`} />
            )}
        </div>
    ) : null;
};

const FeatureEntry = ({ children }) => {
    return (
        <li>
            <span className={style.symbol}>
                <Icon name="check" />
            </span>
            <span className={style.text}>{children}</span>
        </li>
    );
};

const CurrencyPrefix = ({ currency }) => {
    if (currency === "usd") {
        return <span>&#36;</span>;
    }
    return null;
};

const CurrencySuffix = ({ currency }) => {
    if (!currency || currency === "usd") {
        return null;
    }
    return <span className={style.suffix}>{currency.toUpperCase()}</span>;
};
